import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Link,
  styled,
  Typography,
} from "@schaeffler-commerce/design-system";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "~/utils/url/urlResolver";
import type { TreeNode, UsefulTool } from "~/domains/navigation/types/NavigationTypes";

interface UsefulToolsSectionProps {
  usefulTools: UsefulTool[];
  className?: string;
  navigationLevel: TreeNode;
}

export const UsefulToolsSection = ({ usefulTools, navigationLevel, className }: UsefulToolsSectionProps) => {
  const { t } = useTranslation();
  return (
    <UsefulToolsSurface tracking-aux-useful-tools-marker="true" className={className}>
      <Typography variant={"titleMedium"} component={"p"} sx={{ paddingBlock: 1 }}>
        {t("feature.usefulTools.title")}
      </Typography>
      {usefulTools.map((item) =>
        item.imageUrl ? (
          <UsefulToolsCard key={item.title} item={item} level={navigationLevel.level} />
        ) : (
          <UsefulToolsLink key={item.title} item={item} level={navigationLevel.level} />
        ),
      )}
    </UsefulToolsSurface>
  );
};
const UsefulToolsSurface = styled("section", {
  shouldForwardProp: () => true,
})(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  background: theme.palette.surface.main,
  width: "232px",
  overflow: "auto",
}));
const UsefulToolsCard = ({ item, level }: { item: UsefulTool; level: number }) => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{ color: "inherit" }}
      tracking-aux-useful-tools-name={t(item.title, { lng: "en" })}
      tracking-aux-useful-tools-url={resolveUrl(item.url)}
      tracking-aux-useful-tools-level={level}
    >
      <CardActionArea href={resolveUrl(item.url)} target={item.openInNewTab ? "_blank" : undefined}>
        <CardMedia component="img" image={item.imageUrl} sx={{ aspectRatio: "224 / 100" }} />
        <StyledCardContent>
          <Typography>{t(item.title)}</Typography>
        </StyledCardContent>
      </CardActionArea>
    </Card>
  );
};

const UsefulToolsLink = ({ item, level }: { item: UsefulTool; level: number }) => {
  const { t } = useTranslation();
  return (
    <Link
      href={resolveUrl(item.url)}
      target={item.openInNewTab ? "_blank" : undefined}
      variant={"labelLarge"}
      tracking-aux-useful-tools-name={t(item.title, { lng: "en" })}
      tracking-aux-useful-tools-url={resolveUrl(item.url)}
      tracking-aux-useful-tools-level={level}
    >
      {t(item.title)}
    </Link>
  );
};

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: `${theme.spacing(1)} !important`,
}));
